/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useMemo } from "react";
import { InventoryColumns } from "../../../components/Shared/Table/Columns";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { useStoreDispatch } from "../../../store/hooks";
import { useGetInventoryQuery } from "../../../store/api/devices/deviceApiSlice";
import { setRadioUnitIndexes, setRfFeedIndexes } from "../../../store/api/deviceConfig/reducers";
import { compareVersions, getRadioUnitIndexList, prepareInventoryDataGrid } from "../helpers";
import { initialState } from "../../../store/helpers/helperInitialState";
import { useGetRfFeedsLatestQuery, useGetRfFeedsQuery } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { IFullDeviceDetails, IRfFeed } from "../../../store/api/deviceConfig/types";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { SCOS_VERSION_23_4_0 } from "../../../globals/globals";

export const useInventoryData = () => {
  const location = useLocation();
  const dispatch = useStoreDispatch();
  const locationPath = location?.pathname.split("/");
  const deviceKey = locationPath[2];
  const { inventory = initialState.pagination } = useSelector(paginationModelSelector);
  const deviceDetail: IFullDeviceDetails = useSelector(selectedDeviceSelector);

  const args = {
    filters: `page=${inventory.page.toString()}&size=${inventory.pageSize.toString()}`,
    deviceKey,
  };
  const { data, isLoading, refetch } = useGetInventoryQuery(args);

  const defaultPageSize = {
    page: 0,
    pageSize: 100,
  };

  const rfLatestArgs = { filters: `page=${defaultPageSize.page.toString()}&size=${defaultPageSize.pageSize.toString()}`, deviceKey };

  const rfArgs = { filters: `page=${defaultPageSize.page.toString()}&size=${defaultPageSize.pageSize.toString()}`, deviceKey };

  const { data: getRfFeedLatestList } = useGetRfFeedsLatestQuery(rfLatestArgs, {
    skip: compareVersions(deviceDetail?.swVersion, SCOS_VERSION_23_4_0)
  });

  const { data: getRfFeedList } = useGetRfFeedsQuery(rfArgs, {
    skip: !compareVersions(deviceDetail?.swVersion, SCOS_VERSION_23_4_0)
  });

  useEffect(() => {
    if (!!getRfFeedList?.data && getRfFeedList?.data.length > 0) {
      const rfFeedIndexes = (getRfFeedList?.data.map((item: IRfFeed) => Object.keys(item).map((keyitem) => keyitem === "index") && item.index) as string[]);
      dispatch(setRfFeedIndexes(rfFeedIndexes));
    }
  }, [dispatch, getRfFeedList, getRfFeedList?.data]);

  useEffect(() => {
    if (!!getRfFeedLatestList?.data && getRfFeedLatestList?.data.length > 0) {
      const rfFeedIndexes = (getRfFeedLatestList?.data.map((item: IRfFeed) => Object.keys(item).map((keyitem) => keyitem === "index") && item.index) as string[]);
      dispatch(setRfFeedIndexes(rfFeedIndexes));
    }
  }, [dispatch, getRfFeedLatestList, getRfFeedLatestList?.data]);

  const [inventoryData, radioUnitIndexList] = useMemo(() => {
    const inventoryApiData = data?.data || [];
    const preparedInventoryData = prepareInventoryDataGrid(inventoryApiData);
    const radioUnitIndexList = getRadioUnitIndexList(inventoryApiData);
    return [preparedInventoryData, radioUnitIndexList];
  }, [data?.data]);

  useEffect(() => {
    if (!!data?.data && data?.data.length > 0) {
      // dispatch(setConfigCounts({ [CONFIGUREROUTES.INVENTORY.Path]: data?.totalItems || 0 }));
      dispatch(setRadioUnitIndexes(radioUnitIndexList));
    }
  }, [data?.data, dispatch, radioUnitIndexList]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    data: inventoryData,
    isLoading,
    InventoryColumns,
  };
};